/**
 * usePlayerNames — single source of truth for `playerId → displayName`.
 *
 * Before PR Perf-H1: PlayersList, WinnerCelebration, EndScreen and useWinner
 * each ran the same `select("id, name").in("id", players)` query inside a
 * useEffect keyed on `[answers, players]`. On the Board view that fires on
 * every Realtime payload / tick, so player names were re-fetched 4× per
 * state change although they never change.
 *
 * This hook is owned by `GameProvider` and exposed via `playerNames` on the
 * context. Refetch happens only when the **content** of the ID list changes
 * (sorted-join key), not when its reference changes.
 */

import { useEffect, useState } from "react"
import type { SupabaseClient } from "@supabase/supabase-js"

export const usePlayerNames = (
  playerIds: string[] | null | undefined,
  supabase: SupabaseClient | null
): Record<string, string> => {
  const [names, setNames] = useState<Record<string, string>>({})

  // Stable structural key — only string IDs feed the query. Non-string
  // entries (legacy Fauna `{ ref, data }` objects, still handled by some
  // consumers) are filtered out so this hook can stay focused.
  const stringIds = (playerIds || []).filter(
    (id): id is string => typeof id === "string"
  )
  const idKey = [...stringIds].sort().join("|")

  useEffect(() => {
    if (!supabase || !idKey) return

    let cancelled = false
    const ids = idKey.split("|")
    ;(async () => {
      try {
        const { data, error } = await supabase
          .from("player")
          .select("id, name")
          .in("id", ids)
        if (cancelled) return
        if (error) {
          console.error("[usePlayerNames] fetch error:", error)
          return
        }
        const next: Record<string, string> = {}
        ;(data || []).forEach((row: { id: string; name: string }) => {
          next[row.id] = row.name
        })
        setNames(next)
      } catch (e) {
        if (!cancelled) console.error("[usePlayerNames] fetch error:", e)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [idKey, supabase])

  return names
}
